import { apiClient } from './api';
import type {
  AnalyticsOverview,
  RevenueResponse,
  ProductAnalyticsResponse,
  CustomerAnalyticsResponse,
  AnalyticsFeaturesCheck,
  OrdersByStatusResponse,
  PaymentMethodsResponse,
  RevenueReport,
  AttributionReport,
  OrderIntelligenceReport,
  CustomerIntelligenceReport,
  ProductIntelligenceReport,
  SalesBreakdownReport,
  AbandonedCartsReport,
  AlertsReport,
  OrdersReport,
  ProductsReport,
  CategoriesReport,
  CouponsReport,
  TaxesReport,
  StockReport,
  CustomersReport,
  SiteAnalyticsSettings,
} from '@wooconnect/shared';

export async function getAnalyticsOverview(siteId: string): Promise<AnalyticsOverview> {
  const res = await apiClient.get(`/sites/${siteId}/analytics/overview`);
  return res.data.data;
}

export async function getAnalyticsRevenue(siteId: string, days = 30): Promise<RevenueResponse> {
  const res = await apiClient.get(`/sites/${siteId}/analytics/revenue?days=${days}`);
  return res.data.data;
}

export async function getAnalyticsProducts(siteId: string): Promise<ProductAnalyticsResponse> {
  const res = await apiClient.get(`/sites/${siteId}/analytics/products`);
  return res.data.data;
}

export async function getAnalyticsCustomers(siteId: string): Promise<CustomerAnalyticsResponse> {
  const res = await apiClient.get(`/sites/${siteId}/analytics/customers`);
  return res.data.data;
}

export async function getAnalyticsFeatures(siteId: string): Promise<AnalyticsFeaturesCheck> {
  const res = await apiClient.get(`/sites/${siteId}/analytics/check-features`);
  return res.data.data;
}

export async function getOrdersByStatus(siteId: string): Promise<OrdersByStatusResponse> {
  const res = await apiClient.get(`/sites/${siteId}/analytics/orders-by-status`);
  return res.data.data;
}

export async function getPaymentMethods(siteId: string): Promise<PaymentMethodsResponse> {
  const res = await apiClient.get(`/sites/${siteId}/analytics/payment-methods`);
  return res.data.data;
}

export async function getRevenueReport(
  siteId: string,
  params: { after: string; before: string; interval: string; compare: string; status_is?: string },
): Promise<RevenueReport> {
  const res = await apiClient.get(`/sites/${siteId}/analytics/revenue-report`, { params });
  return res.data.data;
}

export async function getOrdersReport(
  siteId: string,
  params: { after: string; before: string; interval: string; compare: string; status_is?: string },
): Promise<OrdersReport> {
  const res = await apiClient.get(`/sites/${siteId}/analytics/orders-report`, { params });
  return res.data.data;
}

export async function getAttributionReport(
  siteId: string,
  params: { after: string; before: string; compare: string; status_is?: string },
): Promise<AttributionReport> {
  const res = await apiClient.get(`/sites/${siteId}/analytics/attribution-report`, { params });
  return res.data.data;
}

export async function getOrderIntelligenceReport(
  siteId: string,
  params: { after: string; before: string; compare: string; status_is?: string },
): Promise<OrderIntelligenceReport> {
  const res = await apiClient.get(`/sites/${siteId}/analytics/order-intelligence-report`, { params });
  return res.data.data;
}

export async function getCustomerIntelligenceReport(
  siteId: string,
  params: { after: string; before: string; compare: string; status_is?: string },
): Promise<CustomerIntelligenceReport> {
  const res = await apiClient.get(`/sites/${siteId}/analytics/customer-intelligence-report`, { params });
  return res.data.data;
}

export async function getProductIntelligenceReport(
  siteId: string,
  params: { after: string; before: string; status_is?: string; compare?: string },
): Promise<ProductIntelligenceReport> {
  const res = await apiClient.get(`/sites/${siteId}/analytics/product-intelligence-report`, { params });
  return res.data.data;
}

export async function getSalesBreakdown(
  siteId: string,
  params: { after: string; before: string; status_is?: string; compare?: string },
): Promise<SalesBreakdownReport> {
  const res = await apiClient.get(`/sites/${siteId}/analytics/sales-breakdown-report`, { params });
  return res.data.data;
}

export async function getAbandonedCarts(
  siteId: string,
  params: { after: string; before: string },
): Promise<AbandonedCartsReport> {
  const res = await apiClient.get(`/sites/${siteId}/analytics/abandoned-carts-report`, { params });
  return res.data.data;
}

export async function getAlerts(
  siteId: string,
  params: { after: string; before: string; compare?: string; status_is?: string },
): Promise<AlertsReport> {
  const res = await apiClient.get(`/sites/${siteId}/analytics/alerts`, { params });
  return res.data.data;
}

export async function getProductsReport(
  siteId: string,
  params: { after: string; before: string; orderby?: string; perPage?: number; compare?: string; status_is?: string },
): Promise<ProductsReport> {
  const res = await apiClient.get(`/sites/${siteId}/analytics/products-report`, { params });
  return res.data.data;
}

export async function getCategoriesReport(
  siteId: string,
  params: { after: string; before: string; compare?: string },
): Promise<CategoriesReport> {
  const res = await apiClient.get(`/sites/${siteId}/analytics/categories-report`, { params });
  return res.data.data;
}

export async function getCouponsReport(
  siteId: string,
  params: { after: string; before: string; compare?: string; status_is?: string },
): Promise<CouponsReport> {
  const res = await apiClient.get(`/sites/${siteId}/analytics/coupons-report`, { params });
  return res.data.data;
}

export async function getTaxesReport(
  siteId: string,
  params: { after: string; before: string; compare?: string; status_is?: string },
): Promise<TaxesReport> {
  const res = await apiClient.get(`/sites/${siteId}/analytics/taxes-report`, { params });
  return res.data.data;
}

export async function getStockReport(
  siteId: string,
  params?: { type?: string },
): Promise<StockReport> {
  const res = await apiClient.get(`/sites/${siteId}/analytics/stock-report`, { params });
  return res.data.data;
}

export async function getCustomersReport(
  siteId: string,
  params: { after: string; before: string },
): Promise<CustomersReport> {
  const res = await apiClient.get(`/sites/${siteId}/analytics/customers-report`, { params });
  return res.data.data;
}

// Analytics Settings
export async function getAnalyticsSettings(siteId: string): Promise<SiteAnalyticsSettings> {
  const res = await apiClient.get(`/sites/${siteId}/analytics/settings`);
  return res.data.data;
}

export async function updateAnalyticsSettings(
  siteId: string,
  settings: Partial<SiteAnalyticsSettings>,
): Promise<SiteAnalyticsSettings> {
  const res = await apiClient.put(`/sites/${siteId}/analytics/settings`, settings);
  return res.data.data;
}

export async function syncAnalyticsSettings(siteId: string): Promise<SiteAnalyticsSettings> {
  const res = await apiClient.post(`/sites/${siteId}/analytics/settings/sync`);
  return res.data.data;
}
